import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import type {
  BeneficiaryDetailResponse,
  FiscalQuota,
  UpdateFiscalQuotaRequest,
} from "@/domain/beneficiary";
import type { QuotaListItem, QuotaListPage } from "@/domain/fiscal/quotaList";
import { InvalidationEntity } from "../entityDependencies";
import { queryKeys } from "../queryKeys";
import { arraySlot, detailSlot, isObject, nestedSlot } from "../cache/cacheSlots";
import { aggregateSlot, flatAggregates } from "../cache/aggregateSlots";
import { defineEntityCache } from "../cache/entityCache";
import {
  anyOf,
  byUuid,
  distinctPages,
  viewSlot,
  type ViewRules,
  type ViewShape,
} from "../cache/listViews";

export type FiscalQuotaPatch = Partial<Omit<FiscalQuota, "money">>;

function isFiscalQuota(value: unknown): value is FiscalQuota {
  return (
    isObject(value) &&
    typeof value.uuid === "string" &&
    typeof value.fiscal_year === "number" &&
    isObject(value.money)
  );
}

function isBeneficiaryDetail(value: unknown): value is BeneficiaryDetailResponse {
  return isObject(value) && isObject(value.beneficiary) && Array.isArray(value.fiscal_quotas);
}

function mergeQuota<R extends FiscalQuota>(record: R, patch: FiscalQuotaPatch): R {
  return { ...record, ...patch };
}

/** A list row only carries a few quota fields; the rest are derived by the server. */
function mergeListItem(record: QuotaListItem, patch: FiscalQuotaPatch): QuotaListItem {
  return {
    ...record,
    ...(patch.state !== undefined ? { state: patch.state } : {}),
    ...(patch.fiscal_year !== undefined ? { fiscal_year: patch.fiscal_year } : {}),
    ...(patch.linked_projects !== undefined ? { linked_projects: patch.linked_projects } : {}),
  };
}

const quotaPage: ViewShape<QuotaListPage, QuotaListItem, string> = {
  isPage: (value): value is QuotaListPage =>
    isObject(value) && Array.isArray(value.quotas) && typeof value.total_count === "number",
  records: (page) => page.quotas,
  withRecords: (page, quotas, countDelta) => ({
    ...page,
    quotas,
    total_count: page.total_count + countDelta,
  }),
  idOf: (record) => record.uuid,
  total: (page) => page.total_count,
};

export const distinctQuotaPages = distinctPages(quotaPage);

export const quotaRules: ViewRules<QuotaListItem> = {
  accepts: {
    state: anyOf((quota) => quota.state),
    fiscal_year: anyOf((quota) => quota.fiscal_year),
  },
  narrowing: ["search", "incentive_type"],
  order: {
    fiscal_year: (quota) => quota.fiscal_year,
    investments_count: (quota) => quota.investments_count,
  },
  tieBreak: byUuid("asc", (quota) => quota.uuid),
};

const quotaAggregates = flatAggregates<QuotaListItem>({
  sums: { "investments_count:sum": (quota) => quota.investments_count },
  distributions: {
    "state:distribution": (quota) => [quota.state],
    "fiscal_year:distribution": (quota) => [String(quota.fiscal_year)],
  },
  emptyDistribution: {},
});

const idOf = (quota: FiscalQuota) => quota.uuid;

export const fiscalQuotaCache = defineEntityCache<string, FiscalQuotaPatch, QuotaListItem>({
  entity: InvalidationEntity.FISCAL_QUOTA,
  row: mergeListItem,
  slots: (orgId) => [
    viewSlot({
      prefix: queryKeys.beneficiaries.quotaLists(orgId),
      shape: quotaPage,
      merge: mergeListItem,
      parse: queryKeys.beneficiaries.parseQuotaList,
      rules: quotaRules,
    }),
    aggregateSlot({
      prefix: queryKeys.beneficiaries.quotaAggregatesAll(orgId),
      rules: quotaRules,
      parse: queryKeys.beneficiaries.parseQuotaAggregates,
      ...quotaAggregates,
    }),
    detailSlot({
      prefix: queryKeys.beneficiaries.quotas(orgId),
      isRecord: isFiscalQuota,
      idOf,
      merge: mergeQuota,
    }),
    arraySlot({
      prefix: queryKeys.beneficiaries.fiscalQuotaLists(orgId),
      isRecord: isFiscalQuota,
      idOf,
      merge: mergeQuota,
    }),
    nestedSlot({
      prefix: queryKeys.beneficiaries.details(orgId),
      isParent: isBeneficiaryDetail,
      children: (detail) => detail.fiscal_quotas,
      withChildren: (detail, fiscal_quotas) => ({ ...detail, fiscal_quotas }),
      idOf,
      merge: mergeQuota,
    }),
  ],
  records: (orgId, uuid) => [
    queryKeys.beneficiaries.quota(orgId, uuid),
    queryKeys.beneficiaries.fiscalQuotaReports(orgId, uuid),
  ],
});

const money = (amount: number) => amount.toFixed(2);

/** The fields of an update that the quota can show before the server answers. */
export function toFiscalQuotaPatch(data: UpdateFiscalQuotaRequest): FiscalQuotaPatch {
  const { cost_breakdown, ...fields } = data;
  return {
    ...(fields.state !== undefined ? { state: fields.state } : {}),
    ...(fields.default_profitability_percentage !== undefined
      ? { default_profitability_percentage: fields.default_profitability_percentage }
      : {}),
    ...(fields.deduction_rate != null ? { deduction_rate: fields.deduction_rate } : {}),
    ...(fields.total_budget !== undefined ? { total_budget: fields.total_budget } : {}),
    ...(fields.subsidies !== undefined ? { subsidies: fields.subsidies } : {}),
    ...(cost_breakdown !== undefined
      ? {
          cost_breakdown: cost_breakdown
            ? Object.fromEntries(
                Object.entries(cost_breakdown).map(([key, amount]) => [key, money(amount)]),
              )
            : null,
        }
      : {}),
  };
}

function findCachedQuota(
  client: QueryClient,
  orgId: string,
  uuid: string,
): FiscalQuota | undefined {
  const detail = client.getQueryData(queryKeys.beneficiaries.quota(orgId, uuid));
  if (isFiscalQuota(detail)) return detail;
  for (const [, data] of client.getQueriesData({
    queryKey: queryKeys.beneficiaries.details(orgId),
  })) {
    const found = isBeneficiaryDetail(data)
      ? data.fiscal_quotas.find((quota) => quota.uuid === uuid)
      : undefined;
    if (found) return found;
  }
  return undefined;
}

/**
 * Reads a quota as the cache holds it right now, optimistic edits included, so
 * fields the backend replaces whole (costs, subsidies) are rebuilt from the
 * latest state rather than from the render that triggered the edit.
 */
export function useCachedFiscalQuota(orgId: string) {
  const client = useQueryClient();
  return useCallback(
    (quota: FiscalQuota): FiscalQuota => findCachedQuota(client, orgId, quota.uuid) ?? quota,
    [client, orgId],
  );
}
